import { useDispatch } from 'react-redux';
import axios from '../utils/axiosConfig';
import { notify } from 'react-notify-toast';

interface ProjectOption {
    value: string;
    label: string;
}

interface EpicOption {
    value: string;
    label: string;
}

// Define interfaces for the nested status stages structure
interface StatusStageItem {
    [key: string]: string;
}

interface StatusStageTeam {
    [stateName: string]: StatusStageItem;
}

interface StatusStageProject {
    [teamName: string]: StatusStageTeam;
}

interface StatusStages {
    [projectName: string]: StatusStageProject;
}

interface ProjectsResponse {
    message: string;
    projects: Array<{
        project_name: string;
        project_id: string;
    }>;
}

interface EpicsResponse {
    message: string;
    epics: Array<{
        epic_name: string;
        epic_id: string;
    }>;
    status_stages?: StatusStages;
    display_message?: string;
}

interface ImportEpicResponse {
    message: string;
    epic_id: string;
    imported_items_count: number;
}

// Update the StatusStages interface for the import endpoint
interface ImportStatusStages {
  [teamOrCategory: string]: {
    [stateName: string]: string;  // state name -> completion percentage
  };
}

// Add this interface for the update stages request
interface UpdateStagesRequest {
    status_stages: Record<string, Record<string, Record<string, string>>>;
}

interface UpdateStagesResponse {
    message: string;
}

// Add this interface for the Azure settings response
interface AzureSettingsResponse {
    organization?: string;
    api_token?: string;
    project_name?: string;
    is_active?: boolean;
    status_stages?: string | Record<string, any>;
    processing_states?: boolean;
}

const useAzureService = () => {
    const dispatch = useDispatch();

    const showError = (error: any) => {
        // Just show the error message from the backend
        if (error?.response?.data?.detail) {
            notify.show(error.response.data.detail, "error", 3000);
            return;
        }
        
        if (error?.response?.data?.error) {
            notify.show(error.response.data.error, "error", 3000);
            return;
        }
        
        if (error.message) {
            notify.show(error.message, "error", 3000);
            return;
        }
    };

    const fetchProjects = async (): Promise<ProjectOption[]> => {
        try {
            const response = await axios.get<ProjectsResponse>('/integration/azure/projects');
            
            if (response.data && response.data.projects) {
                return response.data.projects.map(project => ({
                    value: project.project_id,
                    label: project.project_name
                }));
            }
            return [];
        } catch (error) {
            showError(error);
            throw error;
        }
    };

    const fetchEpics = async (projectName: string, projectId: string): Promise<{epics: EpicOption[], statusStages?: StatusStages, displayMessage?: string}> => {
        try {
            const response = await axios.post<EpicsResponse>(`/integration/azure/epics`, {
                project_name: projectName,
                project_id: projectId
            });
            
            let epics: EpicOption[] = [];
            if (response.data && response.data.epics) {
                epics = response.data.epics.map(epic => ({
                    value: epic.epic_id,
                    label: epic.epic_name
                }));
            }
            
            return {
                epics,
                statusStages: response.data.status_stages,
                displayMessage: response.data.display_message
            };
        } catch (error) {
            showError(error);
            throw error;
        }
    };

    const importEpic = async (epicId: string, projectName: string, statusStages?: any): Promise<ImportEpicResponse> => {
        try {
            // Always include status_stages, even if it's an empty object
            const payload: any = {
                epic_id: epicId,
                project_name: projectName,
                status_stages: statusStages || {}
            };
            
            const response = await axios.post<ImportEpicResponse>('/integration/azure/import_epic', payload);
            
            notify.show(response.data.message || 'Import successful', "success", 3000);
            return response.data;
        } catch (error) {
            showError(error);
            throw error;
        }
    };

    // Add this function to the useAzureService hook
    const updateStages = async (statusStages: Record<string, Record<string, Record<string, string>>>): Promise<UpdateStagesResponse> => {
        try {
            const payload: UpdateStagesRequest = {
                status_stages: statusStages
            };
            
            const response = await axios.post<UpdateStagesResponse>('/integration/azure/update_stages', payload);
            
            notify.show(response.data.message || 'Status stages updated successfully', "success", 3000);
            return response.data;
        } catch (error) {
            showError(error);
            throw error;
        }
    };

    // Add this function to the useAzureService hook
    const fetchAzureSettings = async (): Promise<{parsedStages: Record<string, any>}> => {
        try {
            const response = await axios.get<AzureSettingsResponse>('/integration/azure_settings');
            
            if (response.data && response.data.status_stages) {
                // Check if it's a string (for backward compatibility)
                if (typeof response.data.status_stages === 'string') {
                    console.error('Received status_stages as a string, but expected an object');
                    try {
                        // Try to parse it anyway
                        return { parsedStages: JSON.parse(response.data.status_stages) };
                    } catch (error) {
                        console.error('Error parsing status_stages string:', error);
                        return { parsedStages: {} };
                    }
                }
                
                // At this point TypeScript knows it's an object
                // console.log('Status stages received from API:', response.data.status_stages);
                return { parsedStages: response.data.status_stages };
            } else {
                // console.log('No status_stages found in response:', response.data);
                return { parsedStages: {} };
            }
        } catch (error) {
            showError(error);
            throw error;
        }
    };

    return {
        fetchProjects,
        fetchEpics,
        importEpic,
        updateStages,
        fetchAzureSettings
    };
};

export default useAzureService; 